/* eslint @typescript-eslint/strict-boolean-expressions:0 */
import {
  type ChartConfiguration,
  type TooltipItem,
  type ChartTypeRegistry
} from 'chart.js'
import { type ConfigProps, hasConfig } from 'components/Dashboard'
import { eachMinuteOfIntervalWithOptions, format, parseISO } from 'date-fns/fp'
import { toNumber } from 'lodash'
import {
  compact,
  filter,
  find,
  isNil,
  isString,
  map,
  max,
  min,
  replace
} from 'lodash/fp'
import log from 'loglevel'
import { type Identifier } from 'react-admin'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const yBiTitle = (
  sourceKey: string
): { display: boolean; text: string; color: string } => ({
  display: true,
  // text: `${sourceKey}あたりの電気使用量(kWh)`,
  text: `${sourceKey}`,
  color: 'black'
})
const yCompareTitle = (
  date: string
): { display: boolean; text: string; color: string } => ({
  display: true,
  text: `${format('M月')(parseISO(date))}時間帯別平均電力量(kWh)`,
  color: 'black'
})
const biLegend = {
  labels: {
    usePointStyle: true,
    generateLabels: (chart: any) =>
      !isNil(chart.data)
        ? // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: any, index: number) => {
            return {
              text: item.label,
              pointStyle: 'circle',
              datasetIndex: index,
              fillStyle: item.backgroundColor,
              strokeStyle: item.backgroundColor,
              fontColor: item.backgroundColor,
              hidden: !(chart.isDatasetVisible(index) as boolean)
            }
          })(chart.data.datasets)
        : null
  }
}
const compareLegend = {
  labels: {
    usePointStyle: true,
    generateLabels: (chart: any) =>
      !isNil(chart.data)
        ? // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: any, index: number) => {
            return {
              text: item.label,
              fillStyle: item.borderColor,
              strokeStyle: item.borderColor,
              fontColor: item.borderColor,
              lineWidth: item.borderWidth,
              lineDash: item.borderDash,
              pointStyle: 'rect',
              datasetIndex: index,
              hidden: !(chart.isDatasetVisible(index) as boolean)
            }
          })(chart.data.datasets)
        : null
  }
}
const biTooltip = (
  xLabel: string,
  yLabel: string
): {
  callbacks: {
    label: (conetxt: TooltipItem<'scatter'>) => string
    afterBody: (conetxt: TooltipItem<'scatter'>) => string[]
  }
} => ({
  callbacks: {
    label: (context: TooltipItem<'scatter'>) => {
      const { name } = context.raw as { name: string }
      return name
    },
    afterBody: (context: TooltipItem<'scatter'>) => {
      const { x, y } = context[0].parsed
      return [
        `${xLabel}：${x.toLocaleString() as string}`,
        `${yLabel}：${y.toLocaleString() as string}`
      ]
    }
  }
})
const biAnnotation = (
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number
): { annotations: Record<string, any> } => ({
  annotations: {
    line1: {
      type: 'line',
      xMin,
      xMax,
      borderColor: 'rgb(0, 0, 0)',
      borderWidth: 2
    },
    line2: {
      type: 'line',
      yMin,
      yMax,
      borderColor: 'rgb(0, 0, 0)',
      borderWidth: 2
    }
  }
})

export const createBiChart = (
  config: ConfigProps,
  date: string,
  electricityData: Array<{ name: string; id: Identifier; amount: number }>,
  // compare: { data: any; sourceKey: string; targetKey: string; dateKey: string },
  compare: { data: any; sourceKey: string; targetKey: string },
  labels: Array<{ label: string; color: string }>
): ChartConfiguration => {
  const { datasets: datasetConfigs } = config
  const radius = 8
  const { data: compareData, targetKey, sourceKey } = compare
  const data = compact(
    map((dataItem: { name: string; id: Identifier; amount: number }) => {
      const { name, id, amount } = dataItem
      const item = find((item: any) => item[targetKey] === name)(compareData)
      if (isNil(item)) return undefined
      const value = isString(item[date])
        ? toNumber(replace(/,/g, '')(item[date]))
        : item[date]
      const x = Math.floor(value * 10) / 10
      // const y = amount / value
      const y = Math.floor(amount * 10) / 10
      return { x, y, name, id }
    })(electricityData)
  )
  const xValues = map((item: { x: number; y: number }) => item.x)(data)
  const maxX = max(xValues) as number
  const minX = min(xValues) as number
  const yValues = map((item: { x: number; y: number }) => item.y)(data)
  const maxY = max(yValues) as number
  const minY = min(yValues) as number
  const suggestedMinX = (Math.ceil((minX - (maxX - minX) / 10) / 10) - 1) * 10
  const suggestedMaxX = (Math.ceil((maxX + (maxX - minX) / 10) / 10) + 1) * 10
  // const suggestedMinY = (Math.ceil((minY - (maxY - minY) / 10) / 10) - 1) * 10
  // const suggestedMaxY = (Math.ceil((maxY + (maxY - minY) / 10) / 10) + 1) * 10
  const suggestedMinY = minY - (maxY - minY)
  const suggestedMaxY = maxY + (maxY - minY)
  const centerX = suggestedMinX + (suggestedMaxX - suggestedMinX) / 2
  const centerY = suggestedMinY + (suggestedMaxY - suggestedMinY) / 2
  const datasets = [
    {
      label: !isNil(labels[2]) ? labels[2].label : '2',
      data: filter(
        (item: { x: number; y: number }) =>
          item.x < (maxX - minX) / 2 + minX &&
          item.y >= (maxY - minY) / 2 + minY
      )(data),
      backgroundColor: hasConfig(config, 'datasets.2.color')
        ? datasetConfigs[2].color
        : ['rgb(0,0,230)'],
      radius
    },
    {
      label: !isNil(labels[3]) ? labels[3].label : '3',
      data: filter(
        (item: { x: number; y: number }) =>
          item.x >= (maxX - minX) / 2 + minX &&
          item.y >= (maxY - minY) / 2 + minY
      )(data),
      backgroundColor: hasConfig(config, 'datasets.3.color')
        ? datasetConfigs[3].color
        : ['rgb(230,230,0)'],
      radius
    },
    {
      label: !isNil(labels[0]) ? labels[0].label : '0',
      data: filter(
        (item: { x: number; y: number }) =>
          item.x < (maxX - minX) / 2 + minX && item.y < (maxY - minY) / 2 + minY
      )(data),
      backgroundColor: hasConfig(config, 'datasets.0.color')
        ? datasetConfigs[0].color
        : ['rgb(230,0,0)'],
      radius
    },
    {
      label: !isNil(labels[1]) ? labels[1].label : '1',
      data: filter(
        (item: { x: number; y: number }) =>
          item.x >= (maxX - minX) / 2 + minX &&
          item.y < (maxY - minY) / 2 + minY
      )(data),
      backgroundColor: hasConfig(config, 'datasets.1.color')
        ? datasetConfigs[1].color
        : ['rgb(0,230,0)'],
      radius
    }
  ]
  const options = {
    type: 'scatter' as keyof ChartTypeRegistry,
    data: {
      datasets
    },
    options: {
      scales: {
        x: {
          ticks: {
            color: 'black'
          },
          title: {
            display: true,
            text: `電力量(kWh)`,
            color: 'black'
          },
          min: suggestedMinX,
          max: suggestedMaxX
        },
        y: {
          title: yBiTitle(sourceKey),
          min: suggestedMinY,
          max: suggestedMaxY
        }
      },
      plugins: {
        legend: biLegend,
        tooltip: biTooltip('電力量(kWh)', sourceKey),
        annotation: biAnnotation(centerX, centerX, centerY, centerY),
        datalabels: {
          display: false
        }
      }
    }
  }
  return options
}
const times = map((date: Date) => format('H:mm')(date))(
  eachMinuteOfIntervalWithOptions({ step: 30 })({
    start: parseISO('1970-01-01T00:00'),
    end: parseISO('1970-01-01T23:30')
  })
)
export const createCompareChart = (
  date: string,
  config: ConfigProps,
  values: Array<Array<number | null>>,
  labels: string[]
): ChartConfiguration => {
  const { datasets } = config
  const options = {
    type: 'line' as keyof ChartTypeRegistry,
    data: {
      labels: times,
      datasets: [
        {
          label: labels[1],
          data: values[1] as Array<number | null>,
          borderColor: hasConfig(config, 'datasets.1.color')
            ? datasets[1].color
            : ['#799EF8'],
          backgroundColor: hasConfig(config, 'datasets.1.color')
            ? datasets[1].color
            : ['#799EF8'],
          tension: 0.1
        },
        {
          label: labels[0],
          data: values[0] as Array<number | null>,
          // fill: {
          //   target: '-1',
          //   above: hasConfig(config, 'datasets.0.fill.above')
          //     ? datasets[0].fill.above
          //     : 'rgb(232, 164, 136)',
          //   below: 'rgb(0, 0, 0, 0)'
          // },
          borderColor: hasConfig(config, 'datasets.0.color')
            ? datasets[0].color
            : ['#F5C342'],
          backgroundColor: hasConfig(config, 'datasets.0.color')
            ? datasets[0].color
            : ['#F5C342'],
          tension: 0.1
        }
      ]
    },
    options: {
      scales: {
        x: {
          ticks: {
            color: 'black'
          }
        },
        y: {
          title: yCompareTitle(date)
        }
      },
      plugins: {
        legend: compareLegend,
        datalabels: {
          display: false
        }
      }
    }
  }
  return options
}
